import { useEffect, useRef, type DependencyList } from "react";

// One passive scroll listener + one debounced resize listener for the whole page, batched into a
// single rAF. Subscribers write transforms straight to DOM refs — never setState from here.

type Subscriber = (y: number) => void;

const subs = new Set<Subscriber>();
let ticking = false;
let resizeTimer: ReturnType<typeof setTimeout> | undefined;

const run = () => {
  ticking = false;
  const y = window.scrollY;
  subs.forEach((fn) => fn(y));
};

const onScroll = () => {
  if (ticking) return;
  ticking = true;
  requestAnimationFrame(run);
};

const onResize = () => {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(onScroll, 120);
};

export function subscribe(fn: Subscriber): () => void {
  if (typeof window === "undefined") return () => {};
  if (subs.size === 0) {
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
  }
  subs.add(fn);
  fn(window.scrollY);
  return () => {
    subs.delete(fn);
    if (subs.size === 0) {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      clearTimeout(resizeTimer);
    }
  };
}

/** Subscribes `fn` to the shared scroll loop; re-subscribes when `deps` change. */
export function useScrollLoop(fn: Subscriber, deps: DependencyList = []): void {
  const fnRef = useRef(fn);
  useEffect(() => {
    fnRef.current = fn;
  });

  useEffect(
    () => subscribe((y) => fnRef.current(y)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caller-controlled re-subscribe
    deps,
  );
}
